import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MdDirections } from 'react-icons/md';
import { SlArrowRight } from "react-icons/sl";
import { FiShare2 } from "react-icons/fi";
import MobileTraderbrand from './MobileTraderbrand';
import { AiOutlineArrowLeft, AiOutlineCheckCircle, AiOutlineExclamationCircle } from 'react-icons/ai';
import { client, urlFor } from '../sanity/sanityClient';
import { useServerRole } from '../utils/permissions';
import './TraderBrand.css';

const categories = [
  "clothingandfashion", "electronics", "arts", "fashion", "truck2cargo" , "salon", "grocery",
  "beauty", "crops", "useditems", "kidsandtoys", "sports", "kitchen", "hotels",
  "hardware", "livestock", "books", "estate", "foodandbeverages", "agritech",
  "homeandfurnitures", "vehicles", "jewelryandaccessories", "medical",
  "giftoptions", "energyandfuel", "servicesandinstitutions", "eventsandnightclubs",
  "booksandstationery", "cuisines"
];

const TraderBrand = () => {
  const { userbrandId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 768px)").matches
  );

  // The viewer's real identity: id from the cached login object (UI
  // decision only), admin status from the server.
  const { isAdmin: viewerIsAdmin, hasPermission } = useServerRole();
  const cachedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY) || 'null');
    } catch {
      return null;
    }
  })();
  const viewerOwnId = cachedUser?._id ? String(cachedUser._id) : null;

  // Single source of truth for "may this viewer modify this product?".
  // Owner of THE PRODUCT (product.user), or an admin with manage_products.
  // The URL param is never used for this decision.
  const canManageProduct = (product) => {
    if (!product) return false;
    const isOwner = !!viewerOwnId && String(product.user) === viewerOwnId;
    const isModerator = !!viewerIsAdmin && hasPermission('manage_products');
    return isOwner || isModerator;
  };

  const viewerIsAdminOnOwnShop = !!viewerIsAdmin && !!viewerOwnId && userbrandId === viewerOwnId;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let query;
        let params = {};

        if (viewerIsAdminOnOwnShop && selectedCategory) {
          query = `*[_type == $selectedCategory]`;
          params = { selectedCategory };
        } else if (viewerIsAdminOnOwnShop) {
          query = `*[_type in $categories]`;
          params = { categories };
        } else {
          // Everyone else (including an admin viewing someone else's
          // shop) sees only that shop's products, read-only unless
          // canManageProduct() says otherwise per product.
          query = `*[_type in $categories && user == $userbrandId]`;
          params = { categories, userbrandId };
        }

        const result = await client.fetch(query, params);
        setProducts(result);
      } catch (error) {
        console.error("Error fetching products:", error);
        showToastNotification('Error fetching products', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [userbrandId, selectedCategory, viewerIsAdminOnOwnShop]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const showToastNotification = (message, type = 'success') => {
    setToastMessage({ message, type });
    setShowToast(true);
  };

  // Defense in depth: even if a button were somehow rendered, the
  // handlers re-check permission against the product itself before
  // calling Sanity. NOTE: this is still client-side only — see the
  // security note after the code.
  const deleteProduct = async (productId) => {
    const product = products.find((p) => p._id === productId);
    if (!canManageProduct(product)) {
      showToastNotification('You are not allowed to delete this product', 'error');
      return;
    }

    try {
      await client.delete(productId);
      setProducts((prev) => prev.filter((p) => p._id !== productId));
      showToastNotification('Product deleted successfully', 'success');
    } catch (error) {
      console.error("Error deleting product:", error);
      showToastNotification('Failed to delete product', 'error');
    }
  };

  const updateProductStatus = async (productId, newStatus) => {
    const product = products.find((p) => p._id === productId);
    if (!canManageProduct(product)) {
      showToastNotification('You are not allowed to update this product', 'error');
      return;
    }

    try {
      await client.patch(productId).set({ status: newStatus }).commit();
      setProducts((prev) => prev.map((p) => (p._id === productId ? { ...p, status: newStatus } : p)));
      showToastNotification(`Product marked as ${newStatus}`, 'success');
    } catch (error) {
      console.error("Error updating product status:", error);
      showToastNotification("Failed to update product status", 'error');
    }
  };

  if (loading) return <p className="noUserProducts">Loading products...</p>;

  const handleGetDirections = (productLocation) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLatitude = position.coords.latitude;
          const userLongitude = position.coords.longitude;
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLatitude},${userLongitude}&destination=${productLocation.lat},${productLocation.lng}&travelmode=driving`;
          window.open(directionsUrl, "_blank", "noopener,noreferrer");
        },
        (error) => {
          console.error("Error fetching user location:", error);
          alert("Unable to fetch your location. Please allow location access.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  return (
    <div className="trader-brand-container">
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: toastMessage?.type === 'error' ? 'rgba(220, 38, 38, 0.95)' : 'rgba(0, 0, 0, 0.85)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          animation: 'fadeIn 0.3s ease-in-out',
          maxWidth: '90%',
          textAlign: 'center'
        }}>
          {toastMessage?.type === 'error' ? (
            <AiOutlineExclamationCircle size={24} color="#fef2f2" />
          ) : (
            <AiOutlineCheckCircle size={24} color="#4caf50" />
          )}
          <div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              {toastMessage?.type === 'error' ? 'Error!' : 'Success!'}
            </div>
            <div style={{ fontSize: '14px' }}>
              {toastMessage?.message}
            </div>
          </div>
        </div>
      )}

      <div
        className="fixed-header"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'transparent',
          padding: '10px 15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '50px',
          boxSizing: 'border-box'
        }}
      >
        <Link
          to={'/'}
          style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'black' }}
        >
          <AiOutlineArrowLeft style={{ fontSize: '24px', marginRight: '8px' }} />
          <span style={{ fontSize: '16px', fontWeight: '500' }}>Back to Home</span>
        </Link>

        <button
          onClick={() => {
            const shareUrl = `${window.location.origin}/#/shop/${userbrandId}`;

            if (navigator.share) {
              navigator.share({ title: "Visit My Store", text: "Check out this store", url: shareUrl });
            } else {
              navigator.clipboard.writeText(shareUrl);
              alert("Store link copied!");
            }
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', border: 'none', background: '#111', color: '#fff', padding: '8px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
        >
          Share Your Store
          <FiShare2 size={16} />
        </button>
      </div>

      <div className="scrollable-content" style={{ marginTop: '50px', padding: '15px' }}>
        {viewerIsAdminOnOwnShop && (
          <div className="category-scroll">
            <button onClick={() => setSelectedCategory(null)} style={{ padding: "6px 10px", cursor: "pointer" }}>
              ALL
            </button>

            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: "6px 10px",
                  cursor: "pointer",
                  background: selectedCategory === cat ? "#2563eb" : "#eee",
                  color: selectedCategory === cat ? "white" : "black",
                  borderRadius: "4px"
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        <div className="product-list">
          {products.length > 0 ? (
            isMobile ? (
              <MobileTraderbrand
                products={products}
                deleteProduct={deleteProduct}
                updateProductStatus={updateProductStatus}
                showToastNotification={showToastNotification}
                canManageProduct={canManageProduct}
                showAdminTools={viewerIsAdminOnOwnShop}
              />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px', padding: '10px' }}>
                {products.map((product) => (
                  <div key={product._id} className="product-card relative" style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', padding: '15px', textAlign: 'center', position: 'relative' }}>

                    <div className="image-wrapper">
                      {product.image && (
                        <img
                          src={urlFor(product.image[0])}
                          alt={product.name}
                          className="product-image"
                          style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '4px', marginBottom: '10px' }}
                        />
                      )}

                      {viewerIsAdminOnOwnShop && (
                        <div className="image-actions">
                          {product.geolocation && (
                            <button onClick={() => handleGetDirections(product.geolocation)} className="icon-btn direction-icon">
                              <MdDirections size={16} />
                            </button>
                          )}

                          {product.user && (
                            <Link
                              to={{ pathname: `/chat/${product.user}`, state: { productImage: product.image?.[0]?.asset?._ref } }}
                              className="icon-btn message-icon"
                            >
                              <SlArrowRight size={14} />
                            </Link>
                          )}
                        </div>
                      )}
                    </div>

                    {product.status && (
                      <span
                        style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          padding: '4px 8px',
                          fontSize: '10px',
                          fontWeight: '600',
                          borderRadius: '4px',
                          backgroundColor: product.status === "available" ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)',
                          color: 'white'
                        }}
                      >
                        {product.status.toUpperCase()}
                      </span>
                    )}

                    <h3 className="product-name" style={{ margin: '10px 0', fontSize: '16px', fontWeight: '600' }}>
                      {product.name}
                    </h3>
                    <p className="product-price" style={{ margin: '5px 0', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                      {product.currency || 'TZS'}:<strong>{product.price?.toLocaleString()}/=</strong>
                    </p>

                    {product.geolocation && (
                      <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                        {product.location}
                      </p>
                    )}

                    {canManageProduct(product) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                        <button
                          onClick={() => deleteProduct(product._id)}
                          className="delete-button"
                          style={{ padding: '8px 12px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                        >
                          Delete
                        </button>

                        <button
                          onClick={() => updateProductStatus(product._id, product.status === "available" ? "soldout" : "available")}
                          className="status-button"
                          style={{ padding: '8px 12px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                        >
                          Mark as{" "}
                          {product.status === "available" ? "Sold Out" : "Available"}
                        </button>
                      </div>
                    ) : (
                      <Link
                        to={`/chat/${product.user}`}
                        style={{ padding: '8px 12px', backgroundColor: '#28a745', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '4px', textDecoration: 'none', display: 'inline-block', marginTop: '10px', fontSize: '12px' }}
                      >
                        Message
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            <p className="noUserProducts" style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#666' }}>
              No products found for this user.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TraderBrand;